/**
 * TASK-830 — why this holding conflicts, and what the advisor would hold instead.
 *
 * Today "Review conflicts" on a DNA alert lands on `?tab=dna`, which renders the values radar
 * and a 13-item value list. The 31 conflicting holdings the alert card named — Tyson Foods,
 * Broadcom, Meta, Alphabet, NVIDIA, CHF 4'683'029 — **appear nowhere on the destination**. The
 * advisor reconstructs by hand the case they just left.
 *
 * The reasoning they need already exists, per position, on the server:
 *
 *   * `fit_swap._build_dna_reason` → *"Resolves luxury exclusion; aligns with sustainability
 *     tilt"*
 *   * `fit_swap._build_keep_reason` → *"All 7 CIO BUY candidate(s) in Consumer Staples (Food)
 *     hit the luxury exclusion"*
 *
 * Both are persisted on `swap_proposals.dna_reason` and returned by the swaps API. Only
 * `BeforeAfter` rendered them — a whole-client panel with no focus, which is why launching
 * from Amazon opened on Tyson Foods with Amazon 11th of 11.
 *
 * What this widget adds that `SignalInvestigation` never did: it **names the preference**.
 * That panel renders only *"{n} positions conflict with this client's stated preferences"*
 * (`:173-185`) and never says WHICH — Julian has five red lines and the advisor is not told
 * which one Amazon breaches.
 */

import { AlertTriangle, ShieldCheck } from 'lucide-react';

import { WidgetContainer } from '../WidgetContainer';
import { useFit } from '../../../hooks/useFit';
import { useClientDna } from '../../../hooks/useClientDna';
import { useCioCandidates } from '../../../hooks/useCio';
import { useTagOverrides } from '../../../hooks/useTagOverrides';
import TagProvenanceChip from '../../portfolio/TagProvenanceChip';
import TagCorrection from '../../portfolio/TagCorrection';
import type { FitHolding } from '../../../services/portfolioFitService';
import type { SignalScope } from '../../../types/signalScope';

const chf = (v: number | null | undefined) =>
  v == null ? '—' : `CHF ${Math.round(Number(v)).toLocaleString('de-CH').replace(/,/g, "'")}`;

export function DnaConflict({ clientId, focusIsin }: SignalScope) {
  const fit = useFit(clientId);
  const dna = useClientDna(clientId);
  const overrides = useTagOverrides(clientId);

  const conflicts = (fit.data?.holdings ?? []).filter((h: FitHolding) => h.fit_score === 0);
  // The instrument the advisor clicked is the SUBJECT — not row 11 of a list they must scan.
  const subject =
    (focusIsin && conflicts.find((h) => h.isin === focusIsin)) || conflicts[0] || null;

  const exclusionTags = (subject?.conflicts ?? [])
    .filter((c) => c.impact === 'exclusion')
    .map((c) => c.tag);

  // The client's own words. `GET /{client}/dna` returns each exclusion as {text, tag}, so the
  // sentence behind the tag is available and was never shown beside the conflict it caused.
  const redLines = (dna.data?.exclusions ?? []).filter(
    (e) => e.tag && exclusionTags.includes(e.tag),
  );

  const candidates = useCioCandidates(
    subject?.industry_group
      ? { sac: subject.sub_asset_class ?? undefined, ig: subject.industry_group, clientId }
      : {},
  );
  const compliant = (candidates.data?.candidates ?? []).filter((c) => c.clears_exclusions !== false);
  const activeOverrides = (overrides.data?.overrides ?? []).filter((o) => !o.reverted_at);

  return (
    <WidgetContainer title="DNA conflict" source="Portfolio · values fit">
      {(fit.isLoading || dna.isLoading) && (
        <div className="h-24 w-full animate-pulse rounded bg-accent" />
      )}

      {!fit.isLoading && !subject && (
        <div className="flex items-center gap-3 py-6 text-[13px] text-muted-foreground">
          <ShieldCheck className="h-5 w-5 shrink-0 text-emerald-600" />
          No holding conflicts with this client&apos;s values.
        </div>
      )}

      {subject && (
        <div className="space-y-3">
          {/* THE SUBJECT — the instrument the signal was about. */}
          <div data-testid="dna-subject" className="rounded-md border border-destructive/30 bg-destructive/5 p-3">
            <p className="text-[14px] font-semibold text-foreground">
              {subject.security_name ?? subject.isin}
            </p>
            <p className="text-[11px] text-muted-foreground">
              {subject.isin} · {chf(subject.market_value)}
              {conflicts.length > 1 && (
                <span className="ml-1">· 1 of {conflicts.length} conflicting holdings</span>
              )}
            </p>
          </div>

          {/* BOTH HALVES of the conflict — the instrument's tag AND the client's red line. */}
          <div>
            <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Why this conflicts
            </p>
            <div className="mb-1.5 flex flex-wrap gap-1" data-testid="dna-instrument-tags">
              {exclusionTags.map((tag) => (
                <TagProvenanceChip key={tag} tag={tag} provenance={subject.tag_provenance?.[tag]} />
              ))}
            </div>
            {/*
              TASK-837: seeing that the tag is a sector default is TASK-827a's half; correcting
              it is this one. The gesture sits here — beside the tag and the client's own words
              — because that is where an advisor can tell the two apart. It is offered per tag,
              per holding, and deliberately NOT in bulk: a clear-all button would turn an
              attested judgement into a gesture.
            */}
            <div className="mb-1.5 space-y-1" data-testid="dna-tag-corrections">
              {exclusionTags.map((tag) => (
                <TagCorrection
                  key={tag}
                  clientId={clientId}
                  isin={subject.isin}
                  tag={tag}
                  provenance={subject.tag_provenance?.[tag]}
                />
              ))}
            </div>
            {redLines.length > 0 ? (
              <ul data-testid="dna-red-lines" className="space-y-1">
                {redLines.map((e) => (
                  <li key={e.text} className="text-[12px] text-foreground">
                    <span className="text-muted-foreground">The client said:</span>{' '}
                    &ldquo;{e.text}&rdquo;
                  </li>
                ))}
              </ul>
            ) : (
              // Never "a conflict exists" with nothing behind it — that IS the defect.
              <p data-testid="dna-red-line-missing" className="text-[12px] text-muted-foreground">
                The client&apos;s stated preference behind {exclusionTags.join(', ') || 'this tag'}{' '}
                could not be found in their DNA — so this conflict cannot be explained from
                their own words.
              </p>
            )}
          </div>

          {/* WHAT THEY WOULD HOLD INSTEAD — queried live, not read from stored proposals. */}
          <div>
            <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              What the house would hold instead
            </p>
            {candidates.isLoading && <div className="h-4 w-40 animate-pulse rounded bg-accent" />}
            {!candidates.isLoading && compliant.length === 0 ? (
              // "No compliant swap" is a FIRST-CLASS outcome, not an empty state.
              <div data-testid="dna-no-swap" className="rounded-md border border-amber-500/20 bg-amber-500/10 p-2.5">
                <p className="text-[12px] font-medium text-amber-800">
                  <AlertTriangle className="mr-1 inline h-3.5 w-3.5" />
                  No CIO BUY candidate in {subject.industry_group ?? 'this bucket'} clears this
                  client&apos;s exclusions.
                </p>
                <p className="mt-1 text-[11px] text-amber-800/90">
                  So the alternatives are: discuss the red line with the client, record a
                  documented exception, or re-derive their DNA if the preference has changed.
                </p>
              </div>
            ) : (
              <ul className="space-y-1">
                {compliant.slice(0, 5).map((c) => (
                  <li key={c.isin} data-testid="dna-candidate" className="text-[12px] text-foreground">
                    {c.issuer ?? c.isin}
                    {c.fit_gain != null && (
                      <span className="ml-1 font-mono text-[11px] text-emerald-600">
                        +{Math.round(c.fit_gain * 100)}pp fit
                      </span>
                    )}
                    {c.cio_view && (
                      <span className="ml-1 text-[11px] text-muted-foreground">· {c.cio_view}</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/*
            TASK-830 / TASK-820: portfolio signals have no `resolve` disposition, and refinement
            established that the reason is NOT what the tickets originally quoted — `resolve` is
            absent from all three portfolio policies, which share one tuple, because a CONDITION
            is closed by the system retracting it on re-evaluation, never by a user declaring it
            closed.
            The advisor-facing sentence for that lives on the catalogue as
            `absent_disposition_notes` (TASK-820/DEC-1) and is awaiting sign-off. Until it
            exists this states the CLOSURE RULE — which is a fact about the machine, not a
            compliance judgement about a person — and does not paraphrase a comment that
            explains a different decision.
          */}
          <p data-testid="dna-closure-rule" className="border-t border-border pt-2 text-[11px] text-muted-foreground">
            This conflict closes when the holding changes or the client&apos;s profile does —
            it is re-evaluated on every tick, so there is nothing here to mark resolved.
          </p>

          {/*
            TASK-837: every correction standing on this client, listed where it can be
            WITHDRAWN.

            It is here rather than beside the tag because a REMOVED exclusion takes its holding
            out of the conflict list altogether — the widget above stops rendering it, which is
            correct (it no longer conflicts) and would leave the advisor no way back to their
            own decision. A reversal that cannot be reached is not a reversal.
          */}
          {activeOverrides.length > 0 && (
            <div data-testid="dna-active-corrections" className="border-t border-border pt-2">
              <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Your corrections for this client
              </p>
              <ul className="space-y-1">
                {activeOverrides.map((o) => (
                  <li key={o.id} className="text-[11px] text-muted-foreground">
                    <span className="text-foreground">{o.tag}</span>{' '}
                    {o.direction === 'remove' ? 'removed from' : 'applied to'} {o.isin}
                    {o.rationale && <> — &ldquo;{o.rationale}&rdquo;</>}
                    <span className="ml-1">
                      <TagCorrection
                        clientId={clientId}
                        isin={o.isin}
                        tag={o.tag}
                        provenance={{
                          source: 'advisor_override',
                          basis: o.direction,
                          override_id: o.id,
                          actor: o.actor,
                          at: o.at,
                          rationale: null,
                        }}
                      />
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </WidgetContainer>
  );
}

export default DnaConflict;
